import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api";
import "./ProductDetails.css";
function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [variantId, setVariantId] = useState("");
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const [wishlistSaved, setWishlistSaved] = useState(false);

  const fetchProduct = useCallback(() => {
    API.get(`/products/${id}`)
      .then((res) => { setProduct(res.data); setVariantId(res.data.variants?.[0]?._id || ""); })
      .catch((err) => console.log(err));
  }, [id]);

  useEffect(() => {
    fetchProduct();
    API.get("/wishlist")
      .then((res) =>
        setWishlistSaved(res.data.some((item) => item._id === id))
      )
      .catch(() => setWishlistSaved(false));
  }, [id, fetchProduct]);

  const addToCart = async () => {
    try {
      await API.post("/cart", {
        productId: product._id,
        quantity,
        variantId: variantId || undefined,
      });

      alert("Added to Cart!");
      navigate("/cart");
    } catch (error) {
      // Wishlist feature: retain login guidance only for a true unauthorized response.
      if (error.response?.status === 401) {
        alert("Please login first");
        navigate("/auth");
      } else {
        alert(error.response?.data?.message || "Unable to update wishlist. Please try again.");
      }
    }
  };

  const toggleWishlist = async () => {
    try {
      if (wishlistSaved) {
        await API.delete(`/wishlist/${product._id}`);
        setWishlistSaved(false);
      } else {
        await API.post("/wishlist", { productId: product._id });
        setWishlistSaved(true);
      }
    } catch (error) {
      alert("Please login first");
      navigate("/auth");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();

    try {
      await API.post(`/products/${product._id}/reviews`, review);
      setReview({ rating: 5, comment: "" });
      fetchProduct();
      alert("Review saved");
    } catch (error) {
      alert(error.response?.data?.message || "Unable to save review");
    }
  };

  if (!product) return <h2>Loading...</h2>;
  const selectedVariant = product.variants?.find((item) => item._id === variantId);

  // Interview feature: a familiar rating breakdown helps customers judge product quality at a glance.
  const ratingBreakdown = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: product.reviews?.filter((item) => Number(item.rating) === rating).length || 0,
  }));

 return (
  <div className="product-detail-container">
    <div className="product-detail-card">

      {/* IMAGE SECTION */}
      <div className="product-image">
        <img src={product.image} alt={product.name} />
      </div>

      {/* INFO SECTION */}
      <div className="product-info">
        <h2 className="product-title">{product.name}</h2>

        <p className="price">Rs. {selectedVariant?.price ?? product.discountPrice ?? product.price}</p>
        <p className="product-rating">
          ★ {Number(product.rating || 0).toFixed(1)} from {product.numReviews || 0} reviews
        </p>

        <p className="desc">{product.description}</p>
        {product.variants?.length > 0 && <label className="variant-picker">Choose option<select value={variantId} onChange={(e) => { setVariantId(e.target.value); setQuantity(1); }}>{product.variants.map((variant) => <option key={variant._id} value={variant._id}>{variant.name} — Rs. {variant.price} ({variant.stock} available)</option>)}</select></label>}
        {product.vratFriendly && <p className="vrat-note"><strong>Seller-declared vrat friendly.</strong> Fasting practices vary; verify the listed ingredients for your tradition.</p>}
        {product.ingredients?.length > 0 && <p><strong>Ingredients:</strong> {product.ingredients.join(", ")}</p>}

        {/* Quantity */}
        <div className="quantity-control">
          <button
            onClick={() => quantity > 1 && setQuantity(quantity - 1)}
          >
            -
          </button>
          <span>{quantity}</span>
          <button
            onClick={() => setQuantity(quantity + 1)}
          >
            +
          </button>
        </div>

        {/* Add Button */}
        <button
          className="add-btn"
          onClick={addToCart}
          disabled={(selectedVariant ? selectedVariant.stock : product.stock) === 0}
        >
          Add to Cart
        </button>

        <button className="wishlist-detail-btn" onClick={toggleWishlist}>
          {wishlistSaved ? "Saved to Wishlist" : "Save to Wishlist"}
        </button>

        {/* Stock Status */}
        <p
          className={
            product.stock > 0 ? "stock in-stock" : "stock out-stock"
          }
        >
          {product.stock > 0 ? "In Stock" : "Out of Stock"}
        </p>

      </div>
    </div>

    <section className="reviews-section">
      <div className="reviews-list">
        <h3>Customer Reviews</h3>
        <div className="rating-breakdown">
          {ratingBreakdown.map((item) => (
            <span key={item.rating}>{item.rating}★: {item.count}</span>
          ))}
        </div>
        {product.reviews?.length ? (
          product.reviews.map((item) => (
            <div className="review-card" key={item._id}>
              <strong>{item.name}</strong>
              <span>★ {item.rating}</span>
              <p>{item.comment}</p>
            </div>
          ))
        ) : (
          <p>No reviews yet.</p>
        )}
      </div>

      <form className="review-form" onSubmit={submitReview}>
        <h3>Write a Review</h3>
        <select
          value={review.rating}
          onChange={(e) => setReview({ ...review, rating: e.target.value })}
        >
          <option value="5">5 - Excellent</option>
          <option value="4">4 - Very good</option>
          <option value="3">3 - Good</option>
          <option value="2">2 - Fair</option>
          <option value="1">1 - Poor</option>
        </select>
        <textarea
          value={review.comment}
          onChange={(e) => setReview({ ...review, comment: e.target.value })}
          placeholder="Share your experience after delivery"
          required
        />
        <button type="submit">Submit Review</button>
      </form>
    </section>
  </div>
);
}

export default ProductDetails;
